import backendAxios from "../api/backend/axios";
import { extractJanS3Key, markdownToPlainText } from "./index";
import { markdownToHtml } from "./docExport";

type ClipboardItemData = Blob | Promise<Blob>;
type ClipboardItemInput = Record<string, ClipboardItemData>;

interface ClipboardWriter {
  write(data: ClipboardItem[]): Promise<void>;
  writeText?(text: string): Promise<void>;
}

interface ClipboardItemConstructor {
  new (items: ClipboardItemInput): ClipboardItem;
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Failed to convert blob to data URL"));
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("Failed to read blob"));
    reader.readAsDataURL(blob);
  });
}

async function fetchJanImageAsDataUrl(s3Key: string): Promise<string> {
  const isSvg = s3Key.endsWith(".svg");
  const endpoint = isSvg ? "/api/s3-image/convert" : "/api/s3-image";
  const response = await backendAxios.get(endpoint, {
    params: { key: s3Key },
    responseType: "blob",
  });

  const mimeType = isSvg
    ? "image/png"
    : response.data.type || response.headers["content-type"] || "image/png";

  return blobToDataUrl(new Blob([response.data], { type: mimeType }));
}

const EXTERNAL_IMAGE_FETCH_TIMEOUT_MS = 5000;

async function fetchExternalImageAsDataUrl(url: string): Promise<string> {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    EXTERNAL_IMAGE_FETCH_TIMEOUT_MS
  );

  let response: Response;
  try {
    response = await fetch(url, { signal: controller.signal });
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error(
        `Image fetch timed out after ${EXTERNAL_IMAGE_FETCH_TIMEOUT_MS}ms: ${url}`
      );
    }
    throw error;
  }
  clearTimeout(timeoutId);

  if (!response.ok) {
    throw new Error(`Failed to fetch image for clipboard: ${response.status}`);
  }

  return blobToDataUrl(await response.blob());
}

export async function inlineImagesForClipboard(
  htmlContent: string
): Promise<string> {
  if (!htmlContent.includes("<img")) {
    return htmlContent;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");
  const images = Array.from(doc.querySelectorAll("img"));

  if (images.length === 0) {
    return htmlContent;
  }

  const dataUrlCache = new Map<string, string>();

  await Promise.all(
    images.map(async (image) => {
      const src = image.getAttribute("src");
      if (!src || src.startsWith("data:")) {
        return;
      }

      try {
        let dataUrl = dataUrlCache.get(src);
        if (!dataUrl) {
          const janS3Key = extractJanS3Key(src);
          dataUrl = janS3Key
            ? await fetchJanImageAsDataUrl(janS3Key)
            : await fetchExternalImageAsDataUrl(src);
          dataUrlCache.set(src, dataUrl);
        }

        image.setAttribute("src", dataUrl);
      } catch (error) {
        console.warn("[richClipboard] Failed to inline image for clipboard:", {
          src,
          error,
        });
      }
    })
  );

  return doc.body.innerHTML;
}

export function createRichClipboardItems(
  htmlContent: string | Promise<string>,
  plainTextContent: string,
  ClipboardItemCtor: ClipboardItemConstructor = ClipboardItem
): ClipboardItem[] {
  const htmlBlob =
    typeof htmlContent === "string"
      ? new Blob([htmlContent], { type: "text/html" })
      : htmlContent.then((resolvedHtml) => {
          return new Blob([resolvedHtml], { type: "text/html" });
        });

  return [
    new ClipboardItemCtor({
      "text/html": htmlBlob,
      "text/plain": new Blob([plainTextContent], { type: "text/plain" }),
    }),
  ];
}

export async function copyRichHtmlToClipboard(
  htmlContent: string | Promise<string>,
  plainTextContent: string,
  clipboard: ClipboardWriter = navigator.clipboard,
  ClipboardItemCtor: ClipboardItemConstructor = ClipboardItem
): Promise<void> {
  await clipboard.write(
    createRichClipboardItems(htmlContent, plainTextContent, ClipboardItemCtor)
  );
}

export async function copyPlainTextToClipboard(
  content: string,
  clipboard: ClipboardWriter = navigator.clipboard
): Promise<void> {
  if (!clipboard.writeText) {
    throw new Error("Clipboard writeText is not available");
  }

  await clipboard.writeText(content);
}

export async function copyMarkdownAsPlainText(
  markdown: string,
  clipboard: ClipboardWriter = navigator.clipboard
): Promise<void> {
  await copyPlainTextToClipboard(markdownToPlainText(markdown), clipboard);
}

export async function copyMarkdownAsRichText(
  markdown: string,
  clipboard: ClipboardWriter = navigator.clipboard,
  ClipboardItemCtor: ClipboardItemConstructor = ClipboardItem
): Promise<void> {
  const htmlContent = inlineImagesForClipboard(markdownToHtml(markdown));
  const plainTextContent = markdownToPlainText(markdown);
  await copyRichHtmlToClipboard(
    htmlContent,
    plainTextContent,
    clipboard,
    ClipboardItemCtor
  );
}
